import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { pathologies, errorColor, bodyColor } from "../../../../config/constants";
import { parsedDate } from "../../../../config/functions";
import {
  renderAffectationsOptions,
  renderStrokeSideOptions,
  renderStrokeTypeOptions,
  renderStrokeLocationOptions,
  renderPareticSidesOptions,
  renderComorbidityOptions,
} from "../../../../config/dropdownFunctions";
import { isAISNProjectHospital } from "../../../../config/projectFunctions";
// Components
import { PathologyDiv, TitleTextDiv, DataDiv, ColumnDataDiv, InputDataDiv, Label, DateInput } from "./styles";
import Select from "../../../selectInput";
// Assets
import SelectArrow from "../../../../assets/icons/ico_arrow_down.svg";

const Pathology = ({
  disabledInputs,
  pathology,
  setPathology,
  pathologyError,
  setPathologyError,
  strokeSide,
  setStrokeSide,
  strokeSideError,
  setStrokeSideError,
  strokeType,
  setStrokeType,
  strokeTypeError,
  setStrokeTypeError,
  strokeLocation,
  setStrokeLocation,
  strokeLocationError,
  setStrokeLocationError,
  dateOfStroke,
  setDateOfStroke,
  dateOfStrokeError,
  setDateOfStrokeError,
  hasHeminegligence,
  setHasHeminegligence,
  pareticSide,
  setPareticSide,
  comorbidity,
  setComorbidity,
  comorbidityError,
  setComorbidityError,
  personalInfo,
  setPersonalInfo,
  setPathologyInfo,
  patientVersion,
}) => {
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const [t] = useTranslation("global");

  // Set AISN project flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isAISNHospital = await isAISNProjectHospital(hospitalName);
      setIsAISNHospital(isAISNHospital);
    };
    handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [hospitalName]);

  const handleSetInput = (e, setInput, setError, input) => {
    setInput(e.target.value);
    setError && setError(false);
    if (input === "pathology") {
      if (Number(e.target.value) === pathologies.strokeId) {
        setPathologyInfo({
          AFFECTATION_ID: Number(e.target.value),
          STROKE_DATE: parsedDate(e.target.value),
        });
        setPersonalInfo({
          ...personalInfo,
          PARETIC_SIDE: pareticSide,
          VERSION: patientVersion,
        });
      } else {
        setPathologyInfo({
          AFFECTATION_ID: Number(e.target.value),
        });
        setPersonalInfo({
          ...personalInfo,
          PARETIC_SIDE: "NONE",
          VERSION: patientVersion,
        });
        setDateOfStroke("");
      }
    }
    if (input === "dateOfStroke" && Number(pathology) === pathologies.strokeId) {
      setPathologyInfo({
        AFFECTATION_ID: Number(pathology),
        STROKE_DATE: parsedDate(e.target.value),
      });
      setPersonalInfo({
        ...personalInfo,
        PARETIC_SIDE: pareticSide,
        VERSION: patientVersion,
      });
    }
    if (input === "hasHeminegligence") {
      setPersonalInfo({
        ...personalInfo,
        HAS_HEMINEGLIGENCE: e.target.value,
        VERSION: patientVersion,
      });
    }
    if (input === "pareticSide" && Number(pathology) === pathologies.strokeId) {
      setPersonalInfo({
        ...personalInfo,
        PARETIC_SIDE: e.target.value,
        VERSION: patientVersion,
      });
    }
  };

  return (
    <PathologyDiv>
      <TitleTextDiv>
        <p>{t("pathology")}</p>
      </TitleTextDiv>
      <DataDiv>
        <ColumnDataDiv width="385px">
          <InputDataDiv>
            <Label fontWeight="bold">{t("pathology")} *</Label>
            <Select
              value={pathology}
              onChange={(e) => handleSetInput(e, setPathology, setPathologyError, "pathology")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              error={pathologyError}
              disabled={disabledInputs || isAISNHospital}
            >
              {renderAffectationsOptions(t)}
            </Select>
          </InputDataDiv>

          {isAISNHospital && (
            <>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Stroke side")} *</Label>
                <Select
                  value={strokeSide}
                  onChange={(e) => handleSetInput(e, setStrokeSide, setStrokeSideError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={strokeSideError}
                  disabled={disabledInputs}
                >
                  {renderStrokeSideOptions(t)}
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Stroke type")} *</Label>
                <Select
                  value={strokeType}
                  onChange={(e) => handleSetInput(e, setStrokeType, setStrokeTypeError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={strokeTypeError}
                  disabled={disabledInputs}
                >
                  {renderStrokeTypeOptions(t)}
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Stroke location")} *</Label>
                <Select
                  value={strokeLocation}
                  onChange={(e) => handleSetInput(e, setStrokeLocation, setStrokeLocationError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={strokeLocationError}
                  disabled={disabledInputs}
                >
                  {renderStrokeLocationOptions(t)}
                </Select>
              </InputDataDiv>
            </>
          )}

          {Number(pathology) === pathologies.strokeId && (
            <InputDataDiv>
              <Label htmlFor="dateOfStroke" fontWeight="bold">
                {t("pathologyInformation_strokeDateLabel")} *
              </Label>
              <DateInput
                format="##/##/####"
                placeholder="dd/mm/yyyy"
                mask={["d", "d", "m", "m", "y", "y", "y", "y"]}
                value={dateOfStroke}
                onChange={(e) => handleSetInput(e, setDateOfStroke, setDateOfStrokeError, "dateOfStroke")}
                border={dateOfStrokeError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
                disabled={disabledInputs}
              />
            </InputDataDiv>
          )}
        </ColumnDataDiv>
        <ColumnDataDiv width="385px">
          {Number(pathology) === pathologies.strokeId && (
            <>
              <InputDataDiv activeAnimation>
                <Label fontWeight="bold">{t("pathologyInformation_heminegligenceLabel")} *</Label>
                <Select
                  value={hasHeminegligence}
                  onChange={(e) => handleSetInput(e, setHasHeminegligence, null, "hasHeminegligence")}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  disabled={disabledInputs}
                >
                  <option value={0}>{t("no")}</option>
                  <option value={1}>{t("yes")}</option>
                </Select>
              </InputDataDiv>
              <InputDataDiv activeAnimation>
                <Label fontWeight="bold">{t("pathologyInformation_pareticSideLabel")} *</Label>
                <Select
                  value={pareticSide}
                  onChange={(e) => handleSetInput(e, setPareticSide, null, "pareticSide")}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  disabled={disabledInputs}
                >
                  {renderPareticSidesOptions(t)}
                </Select>
              </InputDataDiv>
              {isAISNHospital && (
                <InputDataDiv>
                  <Label fontWeight="bold">{t("Comorbidity")} *</Label>
                  <Select
                    value={comorbidity}
                    onChange={(e) => handleSetInput(e, setComorbidity, setComorbidityError)}
                    padding="5px 35px 5px 10px"
                    width="200px"
                    arrowImg={SelectArrow}
                    backgroundPosition="175px center"
                    error={comorbidityError}
                    disabled={disabledInputs}
                  >
                    {renderComorbidityOptions(t)}
                  </Select>
                </InputDataDiv>
              )}
            </>
          )}
        </ColumnDataDiv>
      </DataDiv>
    </PathologyDiv>
  );
};

export default Pathology;
